import React, { useState, useEffect } from "react";
import { Pencil, Check, X } from "lucide-react";
import { apiGet, apiGetAll, apiPut } from "../igualdad-api.js";
import { useIgualdad, puede } from "../igualdad-store.jsx";

/* ============================================================
   Talention — Ficha de la empresa activa
   Solo los campos que existen en Cliente: nombre, cif, sector,
   n_empleados_aprox, contacto_email y activo. Los recuentos salen de listas
   aparte porque ClienteController::show no carga relaciones.
   ============================================================ */

function FichaEmpresa() {
  const { session, fireToast, loadClientes } = useIgualdad();
  const [cliente, setCliente] = useState(null);
  const [conteos, setConteos] = useState({ puestos: 0, departamentos: 0, centros: 0 });
  const [editando, setEditando] = useState(false);
  const [form, setForm] = useState(null);
  const [errores, setErrores] = useState(null);
  const [guardando, setGuardando] = useState(false);

  const permisos = session.user?.permisos || [];
  const esStaff = Boolean(session.user?.es_staff);
  const puedeEditar = puede("clientes.update", permisos, esStaff);
  const clienteId = session.clienteId;

  useEffect(() => {
    if (!clienteId) return undefined;
    let vivo = true;

    apiGet(`/clientes/${clienteId}`)
      .then((r) => { if (vivo) setCliente(r.data); })
      .catch((e) => { if (vivo) fireToast(e?.message || "No se pudo cargar la empresa."); });

    Promise.all([
      apiGetAll("/puestos", { cliente_id: clienteId }),
      apiGetAll("/departamentos", { cliente_id: clienteId }),
      apiGetAll("/centros-trabajo", { cliente_id: clienteId }),
    ])
      .then(([pu, de, ce]) => {
        if (!vivo) return;
        setConteos({ puestos: pu.length, departamentos: de.length, centros: ce.length });
      })
      .catch(() => { if (vivo) setConteos({ puestos: 0, departamentos: 0, centros: 0 }); });

    return () => { vivo = false; };
  }, [clienteId]);

  function abrirEdicion() {
    setForm({
      nombre: cliente.nombre || "",
      cif: cliente.cif || "",
      sector: cliente.sector || "",
      n_empleados_aprox: cliente.n_empleados_aprox ?? "",
      contacto_email: cliente.contacto_email || "",
      activo: Boolean(cliente.activo),
    });
    setErrores(null);
    setEditando(true);
  }

  async function guardar() {
    setGuardando(true); setErrores(null);
    try {
      const res = await apiPut(`/clientes/${clienteId}`, {
        nombre: form.nombre.trim(),
        cif: form.cif.trim() || null,
        sector: form.sector.trim() || null,
        n_empleados_aprox: form.n_empleados_aprox === "" ? null : Number(form.n_empleados_aprox),
        contacto_email: form.contacto_email.trim() || null,
        activo: Boolean(form.activo),
      });
      setCliente(res.data);
      setEditando(false);
      fireToast("Ficha actualizada.");
      // La lista de empresas del contexto muestra el nombre: se refresca.
      await loadClientes();
    } catch (e) {
      if (e?.status === 422 && e?.errors) setErrores(e.errors);
      else fireToast(e?.message || "No se pudo guardar la ficha.");
    } finally { setGuardando(false); }
  }

  if (!clienteId) return <div className="rb-page"><p className="rb-empty">Elige una empresa para ver su ficha.</p></div>;
  if (!cliente) return <div className="rb-page"><p className="rb-empty">Cargando…</p></div>;

  const error = (campo) => (errores?.[campo] ? <span className="rb-fieldnote rb-alert-bad">{errores[campo][0]}</span> : null);

  return (
    <div className="rb-page">
      <div className="rb-pagehead">
        <div>
          <div className="rb-eyebrow">Empresa</div>
          <h1 className="rb-h1">{cliente.nombre}</h1>
          <p className="rb-sub">
            {/* El rotulo "CIF" va dentro del span: asi el unico elemento cuyo
                texto es exactamente el CIF es la fila de la tabla de abajo. */}
            {cliente.sector || "Sin sector"} · <span className="rb-mono">CIF {cliente.cif || "—"}</span> ·{" "}
            <span className="rb-pill">{cliente.activo ? "Activa" : "Inactiva"}</span>
          </p>
        </div>
        {puedeEditar && !editando ? (
          <button className="rb-btn rb-btn-primary" onClick={abrirEdicion}><Pencil size={14} /> Editar</button>
        ) : null}
      </div>

      <div className="rb-tilerow">
        <div className="rb-tile">
          <div className="rb-tile-h">Empleados</div>
          <div className="rb-tile-big">{cliente.n_empleados_aprox ?? "—"}</div>
          <div className="rb-soft rb-xs">aproximados</div>
        </div>
        <div className="rb-tile">
          <div className="rb-tile-h">Departamentos</div>
          <div className="rb-tile-big" data-testid="recuento-departamentos">{conteos.departamentos}</div>
          <div className="rb-soft rb-xs">áreas activas</div>
        </div>
        <div className="rb-tile">
          <div className="rb-tile-h">Centros de trabajo</div>
          <div className="rb-tile-big" data-testid="recuento-centros">{conteos.centros}</div>
          <div className="rb-soft rb-xs">registrados</div>
        </div>
        <div className="rb-tile">
          <div className="rb-tile-h">Puestos</div>
          <div className="rb-tile-big" data-testid="recuento-puestos">{conteos.puestos}</div>
          <div className="rb-soft rb-xs">del catálogo</div>
        </div>
      </div>

      <div className="rb-card">
        <div className="rb-sechead"><span className="rb-sechead-l">Identidad</span></div>

        {editando ? (
          <div>
            <label className="rb-field"><span>Nombre</span>
              <input className="rb-inp" value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
              {error("nombre")}</label>
            <label className="rb-field"><span>CIF</span>
              <input className="rb-inp" value={form.cif} onChange={(e) => setForm({ ...form, cif: e.target.value })} />
              {error("cif")}</label>
            <label className="rb-field"><span>Sector</span>
              <input className="rb-inp" value={form.sector} onChange={(e) => setForm({ ...form, sector: e.target.value })} />
              {error("sector")}</label>
            <label className="rb-field"><span>Nº de empleados</span>
              <input className="rb-inp" type="number" min={0} value={form.n_empleados_aprox}
                onChange={(e) => setForm({ ...form, n_empleados_aprox: e.target.value })} />
              {error("n_empleados_aprox")}</label>
            <label className="rb-field"><span>Email de contacto</span>
              <input className="rb-inp" type="email" value={form.contacto_email}
                onChange={(e) => setForm({ ...form, contacto_email: e.target.value })} />
              {error("contacto_email")}</label>
            <label className="rb-field rb-inp-gap">
              <input type="checkbox" checked={form.activo} onChange={(e) => setForm({ ...form, activo: e.target.checked })} />
              <span>Empresa activa</span></label>

            <div className="rb-actions">
              <button className="rb-btn" onClick={() => setEditando(false)}><X size={14} /> Cancelar</button>
              <button className="rb-btn rb-btn-primary" disabled={guardando || !form.nombre.trim()} onClick={guardar}>
                <Check size={14} /> {guardando ? "Guardando…" : "Guardar"}
              </button>
            </div>
          </div>
        ) : (
          <table className="rb-table rb-table-compact">
            <tbody>
              <tr><td className="rb-soft">Nombre</td><td><span className="rb-strong">{cliente.nombre}</span></td></tr>
              <tr><td className="rb-soft">CIF</td><td className="rb-mono">{cliente.cif || "—"}</td></tr>
              <tr><td className="rb-soft">Sector</td><td>{cliente.sector || "—"}</td></tr>
              <tr><td className="rb-soft">Nº de empleados</td><td>{cliente.n_empleados_aprox ?? "—"}</td></tr>
              <tr><td className="rb-soft">Email de contacto</td><td>{cliente.contacto_email || "—"}</td></tr>
              <tr><td className="rb-soft">Estado</td><td>{cliente.activo ? "Activa" : "Inactiva"}</td></tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export { FichaEmpresa };
